import { requireOrdinaryEntityType } from '@einhaus/project314-shared';
import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import UserLayout from '@/components/Layout/UserLayout.tsx';
import { showSpinner, hideSpinner } from '@components/Layout/LoadingSpinner';
import Alert from '@components/UI/Alert';
import Card from '@components/UI/Card';
import TradeChart from '@components/Chart/TradeChart';
import TradeHeader from '@components/Trade/TradeHeader';
import { renderTransactionOutput } from '@components/Trade/TradeRenderUtils';
import { getDatePlusMinus } from '@einhaus/meteoric-helpers';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import './VisualizeTrade.tailwind.css';
import type { EntityOptionObjectCompressed, OptionBuyingStrategy } from '@einhaus/project314-types';
import { trpc } from '@/api/trpc.ts';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useTradingRequisites } from '@/hooks/useTradingRequisites';
import { handleTrpcQuery } from '@/api/trpcResponseHandler';
import TradeLogicV2ExitSummary from '@/components/TradeLogic/TradeLogicV2ExitSummary';
import type { TradingStrategy } from '@/types/appRouter';

const VisualizeTrade: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Fetch trading requisites
    const { isLoading: tradingRequisitesLoading, requisites } = useTradingRequisites({
        enabled: !!id,
        errorConfig: {
            fallbackErrorMessage: 'Failed to load trading requisites',
            setError: (msg) => setError(msg)
        }
    });

    // Extract strategies, option buying strategies
    const tradingStrategies = useMemo(() => requisites?.tradingStrategies || [], [requisites]);
    const optionBuyingStrategies = useMemo(() => requisites?.optionBuyingStrategies || [], [requisites]);

    // Fetch trade data with tRPC
    const tradeQuery = useAppQuery(
        {
            ...trpc.user.fetchPortfolioTrade.queryOptions({ id: id ? parseInt(id) : 0 }),
            enabled: !!id && !isNaN(parseInt(id))
        },
        {
            fallbackErrorMessage: 'Failed to load trade data',
            setError: (msg) => setError(msg)
        }
    );

    // Extract trade data
    const trade = useMemo(() => tradeQuery.data?.trade || null, [tradeQuery.data]);

    // Fetch entity details with tRPC
    const entityQuery = useAppQuery({
        ...trpc.entity.details.queryOptions({
            entityType: requireOrdinaryEntityType(trade?.entityType || 'Asset'),
            entityId: trade?.entityId || 0
        }),
        enabled: !!trade
    });

    // Extract entity data
    const entity = useMemo(() => entityQuery.data?.entity || null, [entityQuery.data]);

    // Prepare options to fetch
    const optionsToFetch = useMemo(() => {
        if (!trade?.transactions.length) return [];

        const options: EntityOptionObjectCompressed[] = [];

        for (const transaction of trade.transactions) {
            const optionId = transaction.derivativeId;
            const { derivativeType } = transaction;

            if (trade.entityType !== 'Asset' && trade.entityType !== 'AssetIndex') continue;
            if (!optionId || derivativeType !== 'Option') continue;

            const foundOption = options.find((o) => o.oId === optionId && o.eId === trade.entityId && o.eType === trade.entityType);

            if (!foundOption) options.push({ oId: optionId, eId: trade.entityId, eType: trade.entityType });
            break;
        }

        return options;
    }, [trade]);

    // Fetch options with tRPC
    const optionsQuery = useAppQuery({
        ...trpc.entity.fetchOptions.queryOptions({ options: optionsToFetch }),
        enabled: optionsToFetch.length > 0
    });

    // Extract option data
    const option = useMemo(
        () => (optionsQuery.data?.options && optionsQuery.data.options.length > 0 ? optionsQuery.data.options[0] : null),
        [optionsQuery.data]
    );

    // Derive the trade's strategy from loaded requisites
    const strategy = useMemo<TradingStrategy | null>(() => {
        if (!trade) return null;
        return tradingStrategies.find((s) => s.id === trade.strategyId) ?? null;
    }, [trade, tradingStrategies]);

    // Derive the trade's option buying strategy (if any) from loaded requisites
    const optionBuyingStrategy = useMemo<OptionBuyingStrategy | null>(() => {
        if (!trade?.transactions.length) return null;

        const optionBuyingStrategyId = trade.transactions.find((t) => t.optionBuyingStrategyId)?.optionBuyingStrategyId;
        if (!optionBuyingStrategyId) return null;

        return optionBuyingStrategies.find((s) => s.id === optionBuyingStrategyId) ?? null;
    }, [trade, optionBuyingStrategies]);

    // Prepare chart data parameters
    const chartDataParams = useMemo(() => {
        if (!trade?.transactions.length || !strategy) return null;

        const startDateTime = getDatePlusMinus('-', 14, 'day', 'ymdhms', null, trade.transactions[0].timestamp);
        const endDateTime = getDatePlusMinus('+', 14, 'day', 'ymdhms', null, trade.transactions.at(-1)!.timestamp);

        return {
            entityId: trade.entityId,
            entityType: requireOrdinaryEntityType(trade.entityType),
            duration: strategy.duration,
            startDateTime,
            endDateTime,
            shouldRemoveAfterHours: strategy.afterHoursRemoved
        };
    }, [trade, strategy]);

    // Fetch bars for duration with tRPC
    const barsQuery = useAppQuery({
        ...trpc.entity.fetchBarsForDuration.queryOptions(
            chartDataParams || {
                entityId: 0,
                entityType: 'Asset',
                duration: '1d',
                startDateTime: '',
                endDateTime: '',
                shouldRemoveAfterHours: false
            }
        ),
        enabled: !!chartDataParams
    });

    // Extract bars data
    const chartData = useMemo(() => barsQuery.data?.bars || [], [barsQuery.data]);

    // Chart loading state is derived directly from the bars query
    const chartLoading = barsQuery.isLoading;

    // Using shared renderTransactionOutput from TradeRenderUtils

    // Show loading spinner when component mounts
    useEffect(() => {
        showSpinner();

        // Hide spinner when component unmounts
        return () => {
            hideSpinner();
        };
    }, []);

    // Handle query errors
    useEffect(() => {
        if (entityQuery.error && trade) {
            handleTrpcQuery(entityQuery, {
                fallbackErrorMessage: 'Failed to load entity data',
                setError: (msg) => setError(msg)
            });
        }
    }, [entityQuery.error, trade]);

    useEffect(() => {
        if (optionsQuery.error && optionsToFetch.length > 0) {
            handleTrpcQuery(optionsQuery, {
                fallbackErrorMessage: 'Failed to load options data',
                setError: (msg) => setError(msg)
            });
        }
    }, [optionsQuery.error, optionsToFetch.length]);

    useEffect(() => {
        if (barsQuery.error && chartDataParams) {
            handleTrpcQuery(barsQuery, {
                fallbackErrorMessage: 'Failed to load chart data',
                setError: (msg) => setError(msg)
            });
        }
    }, [barsQuery.error, chartDataParams]);

    // Manage loading state
    useEffect(() => {
        const isLoading =
            tradingRequisitesLoading ||
            tradeQuery.isLoading ||
            (trade && entityQuery.isLoading) ||
            (optionsToFetch.length > 0 && optionsQuery.isLoading) ||
            (chartDataParams && barsQuery.isLoading);

        setLoading(!!isLoading);

        if (isLoading) {
            showSpinner();
        } else {
            hideSpinner();
        }
    }, [
        tradingRequisitesLoading,
        tradeQuery.isLoading,
        entityQuery.isLoading,
        optionsQuery.isLoading,
        barsQuery.isLoading,
        trade,
        optionsToFetch.length,
        chartDataParams
    ]);

    // Handle ID validation
    useEffect(() => {
        if (!id) {
            setError('No trade ID provided');
        }
    }, [id]);

    if (loading) {
        return (
            <UserLayout title="Visualize Trade - Project 314">
                <div className="my-8">
                    <DataLoadingSpinner text="Loading trade data..." />
                </div>
            </UserLayout>
        );
    }

    if (error) {
        return (
            <UserLayout title="Visualize Trade - Project 314">
                <Alert variant="danger">{error}</Alert>
            </UserLayout>
        );
    }

    if (!trade) {
        return (
            <UserLayout title="Visualize Trade - Project 314">
                <Alert variant="warning">No trade found</Alert>
            </UserLayout>
        );
    }

    const exitCandidateId = trade.exitCandidateId ?? null;
    const exitCandidateHash = trade.exitCandidateHash ?? null;

    return (
        <UserLayout title="Visualize Trade - Project 314">
            <div className="mb-6">
                <h1 className="text-2xl font-semibold text-white mb-4">Trade Visualization</h1>

                <TradeHeader
                    entity={entity}
                    option={option}
                    optionBuyingStrategies={optionBuyingStrategy ? [optionBuyingStrategy] : null}
                    strategy={strategy}
                    trade={trade}
                />

                {strategy && (
                    <TradeLogicV2ExitSummary exitCandidateHash={exitCandidateHash} exitCandidateId={exitCandidateId} strategy={strategy} />
                )}

                <Card className="mb-6">
                    {chartLoading ? (
                        <div className="py-6">
                            <DataLoadingSpinner text="Loading chart data..." />
                        </div>
                    ) : chartData.length > 0 ? (
                        <TradeChart
                            showVolume
                            bars={chartData}
                            duration={strategy?.duration ?? '1d'}
                            entity={entity}
                            height={750}
                            trade={trade}
                        />
                    ) : (
                        <div className="text-center text-zinc-400 py-6">
                            No chart data available for this trade.
                            <div className="mt-2">
                                <span className="text-sm text-zinc-500">
                                    This could be because no bar data exists for this time period or entity.
                                </span>
                            </div>
                        </div>
                    )}
                </Card>

                <Card>
                    <h5 className="text-lg font-medium text-white mb-4">Transactions</h5>
                    <div className="px-4 pb-4">{renderTransactionOutput({ transactions: trade.transactions })}</div>
                </Card>
            </div>
        </UserLayout>
    );
};

export default VisualizeTrade;
